import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { apiURL } from '../../utilities/URLs'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

/**
 * Generic E-Sign Page — standalone signing experience.
 * Loaded via: /e-sign?sessionId=xxx
 *
 * Works with ANY template. Designed to be iframed by parent pages.
 * On completion, posts ESIGN_COMPLETE message to the parent window.
 */
export default function ESignPage() {
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('sessionId')

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [consentGiven, setConsentGiven] = useState(false)
  const [consentLoading, setConsentLoading] = useState(false)
  const [pdfDataUri, setPdfDataUri] = useState('')
  const [interactiveFields, setInteractiveFields] = useState([])
  const [templateName, setTemplateName] = useState('')
  const [numPages, setNumPages] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [submitting, setSubmitting] = useState(false)
  const [completed, setCompleted] = useState(false)

  // Field values state
  const [fieldValues, setFieldValues] = useState({})
  // Signature state: { [fieldId]: base64ImageString }
  const [signedFields, setSignedFields] = useState({})

  // Signature modal
  const [signModalFieldId, setSignModalFieldId] = useState(null)
  const canvasRef = useRef(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasCanvasContent, setHasCanvasContent] = useState(false)

  // Page sizing + scroll
  const containerRef = useRef(null)
  const scrollRef = useRef(null)
  const [pageWidth, setPageWidth] = useState(700)
  const [highlightedFieldId, setHighlightedFieldId] = useState(null)

  useEffect(() => {
    if (!containerRef.current) return

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width
        if (width > 0) setPageWidth(Math.min(width - 32, 1200))
      }
    })

    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [loading, consentGiven])

  useEffect(() => {
    if (signModalFieldId && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d')
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
      setHasCanvasContent(false)
    }
  }, [signModalFieldId])

  useEffect(() => {
    if (sessionId) loadSession()
  }, [])

  const loadSession = async () => {
    setLoading(true)
    setError('')

    try {
      const response = await fetch(`${apiURL}/api/e-sign/sessions/${sessionId}`)
      const data = await response.json()

      if (data.response?.error) {
        if (data.response.error === 'This document has already been signed.') {
          setCompleted(true)
          return
        }
        setError(data.response.error)
        return
      }

      setPdfDataUri(`data:application/pdf;base64,${data.response.pdfBase64}`)
      if (data.response.consentGiven) setConsentGiven(true)
      setTemplateName(data.response.templateName || 'Document')

      const fields = data.response.interactiveFields || []
      setInteractiveFields(fields)

      // Pre-populate editable fields and checkboxes with their default values
      const defaults = {}
      fields.forEach(f => {
        if ((f.type === 'editable' || f.type === 'multiline') && f.defaultValue)
          defaults[f.fieldId] = f.defaultValue
        if (f.type === 'checkbox' && f.defaultValue === 'true')
          defaults[f.fieldId] = true
      })
      setFieldValues(defaults)
    } catch (err) {
      setError('Failed to load document. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const onDocumentLoadSuccess = ({ numPages: pages }) => setNumPages(pages)

  // ─── Required field tracking ───
  const requiredFields = interactiveFields.filter(f => f.isRequired)

  const isFieldComplete = (field) => {
    if (field.type === 'signature') return !!signedFields[field.fieldId]
    if (field.type === 'editable' || field.type === 'multiline') return !!(fieldValues[field.fieldId]?.trim())
    if (field.type === 'checkbox') return fieldValues[field.fieldId] === true || fieldValues[field.fieldId] === 'true'
    return true
  }

  const incompleteRequired = requiredFields.filter(f => !isFieldComplete(f))
  const allComplete = incompleteRequired.length === 0

  // Scale factor: PDF native width is 612pt (US Letter)
  const pdfScale = pageWidth / 612

  const scrollToField = (field) => {
    if (!scrollRef.current) return
    const container = scrollRef.current
    // field.y is a percentage — calculate the pixel offset within the scroll container
    const scrollableHeight = container.scrollHeight
    const targetY = (field.y / 100) * scrollableHeight
    // Center the field in the viewport
    const offset = targetY - container.clientHeight / 3
    container.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' })
  }

  const handleGoToNext = () => {
    if (incompleteRequired.length === 0) return
    const next = incompleteRequired[0]
    setCurrentPage(next.page)
    setHighlightedFieldId(next.fieldId)
    // Auto-clear highlight after 2s
    setTimeout(() => setHighlightedFieldId(null), 2000)
    // Wait for page render then scroll
    setTimeout(() => {
      scrollToField(next)
      if (next.type === 'signature')
        setTimeout(() => setSignModalFieldId(next.fieldId), 300)
    }, 100)
  }

  // ─── Signature drawing ───
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    const clientY = e.touches ? e.touches[0].clientY : e.clientY
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height)
    }
  }

  const startDrawing = (e) => {
    e.preventDefault()
    const ctx = canvasRef.current.getContext('2d')
    const { x, y } = getCanvasCoords(e)
    ctx.beginPath()
    ctx.moveTo(x, y)
    setIsDrawing(true)
    setHasCanvasContent(true)
  }

  const draw = (e) => {
    if (!isDrawing) return
    e.preventDefault()
    const ctx = canvasRef.current.getContext('2d')
    const { x, y } = getCanvasCoords(e)
    ctx.lineTo(x, y)
    ctx.strokeStyle = '#1a3fa0'
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke()
  }

  const stopDrawing = () => setIsDrawing(false)

  const clearCanvas = () => {
    const ctx = canvasRef.current.getContext('2d')
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
    setHasCanvasContent(false)
  }

  const applySignature = () => {
    if (!hasCanvasContent || !signModalFieldId) return
    const signatureImage = canvasRef.current.toDataURL('image/png')
    setSignedFields(prev => ({ ...prev, [signModalFieldId]: signatureImage }))
    setSignModalFieldId(null)
  }

  // ─── Finish ───
  const handleFinish = async () => {
    if (!allComplete) return
    setSubmitting(true)

    try {
      const response = await fetch(`${apiURL}/api/e-sign/sessions/${sessionId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fieldValues,
          signatures: signedFields
        })
      })
      const data = await response.json()

      if (data.response?.error) {
        setError(data.response.error)
        return
      }

      setCompleted(true)

      // Notify parent window (for iframe usage)
      if (window.parent !== window) {
        window.parent.postMessage({ type: 'ESIGN_COMPLETE', sessionId }, '*')
      }
    } catch (err) {
      setError('Failed to complete signing. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const currentPageFields = interactiveFields.filter(f => f.page === currentPage)

  if (!sessionId) {
    return (
      <div className='w-full h-screen flex items-center justify-center bg-white dark:bg-slate-900 transition-colors duration-300'>
        <p className='text-red-600 dark:text-red-400 font-medium'>No signing session specified.</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className='w-full h-screen flex items-center justify-center gap-2 bg-white dark:bg-slate-900 transition-colors duration-300'>
        <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] border-gray-200 dark:border-slate-700 border-t-ertc-primary dark:border-t-ertc-primary h-8 w-8 animate-spin'></div>
        <p className='text-slate-600 dark:text-slate-300'>Loading document...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className='w-full h-screen flex items-center justify-center p-4 bg-white dark:bg-slate-900 transition-colors duration-300'>
        <div className='bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-500/20 rounded-lg p-6 max-w-md text-center'>
          <p className='text-red-600 dark:text-red-400 font-medium mb-4'>{error}</p>
          <button
            onClick={() => { setError(''); loadSession() }}
            className='px-6 py-2 bg-ertc-primary text-white rounded-lg hover:bg-ertc-primary-dark transition-all'
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (completed) {
    return (
      <div className='w-full h-screen flex items-center justify-center p-4 bg-white dark:bg-slate-900 transition-colors duration-300'>
        <div className='bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-500/20 rounded-lg p-8 max-w-md text-center'>
          <div className='text-4xl mb-4'>✓</div>
          <h3 className='text-lg font-bold text-green-800 dark:text-green-400 mb-2'>Document Signed!</h3>
          <p className='text-green-600 dark:text-green-500'>Your {templateName || 'document'} has been successfully signed.</p>
        </div>
      </div>
    )
  }

  // ─── Consent / Intent-to-Sign Screen ───
  if (!consentGiven) {
    return (
      <div className='w-full h-screen overflow-y-auto bg-white dark:bg-slate-900 transition-colors duration-300 p-4 flex flex-col'>
        <div className='w-full max-w-lg m-auto shrink-0 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 overflow-hidden'>
          <div className='bg-ertc-primary px-6 py-5'>
            <h2 className='text-lg font-bold text-white'>Electronic Signature</h2>
            <p className='text-sm text-white/80 mt-0.5'>Please review before proceeding</p>
          </div>

          <div className='px-6 py-6 space-y-5'>
            <div className='space-y-3'>
              <h3 className='text-sm font-bold text-gray-900 dark:text-white'>Consent to Use Electronic Signatures</h3>
              <p className='text-sm text-gray-600 dark:text-gray-300 leading-relaxed'>
                By clicking <strong>"Continue"</strong> below, you agree to use an electronic signature to sign this document. You acknowledge that:
              </p>
              <ul className='text-sm text-gray-600 dark:text-gray-300 space-y-2 pl-1'>
                <li className='flex items-start gap-2'>
                  <span className='text-ertc-primary font-bold mt-0.5'>•</span>
                  <span>Your electronic signature is legally binding and has the same legal effect as a handwritten signature.</span>
                </li>
                <li className='flex items-start gap-2'>
                  <span className='text-ertc-primary font-bold mt-0.5'>•</span>
                  <span>You consent to conducting this transaction electronically under the ESIGN Act and UETA.</span>
                </li>
                <li className='flex items-start gap-2'>
                  <span className='text-ertc-primary font-bold mt-0.5'>•</span>
                  <span>You intend to sign this document and are authorized to do so.</span>
                </li>
                <li className='flex items-start gap-2'>
                  <span className='text-ertc-primary font-bold mt-0.5'>•</span>
                  <span>Your signing activity, including IP address and device information, will be recorded for verification purposes.</span>
                </li>
              </ul>
            </div>

            <div className='bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 border border-gray-100 dark:border-slate-600'>
              <p className='text-xs text-gray-500 dark:text-gray-400 leading-relaxed'>
                This electronic signature process complies with the U.S. Electronic Signatures in Global and National Commerce Act (ESIGN Act) and the Uniform Electronic Transactions Act (UETA). A complete audit trail and Certificate of Evidence will be generated upon completion.
              </p>
            </div>
          </div>

          <div className='px-6 py-4 bg-gray-50 dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 flex items-center justify-end gap-3'>
            <button
              onClick={async () => {
                setConsentLoading(true)
                try {
                  await fetch(`${apiURL}/api/e-sign/sessions/${sessionId}/consent`, {
                    method: 'POST'
                  })
                } catch (err) {
                  console.error('Consent log error:', err)
                }
                setConsentGiven(true)
                setConsentLoading(false)
              }}
              disabled={consentLoading}
              className='px-8 py-2.5 bg-ertc-primary text-white font-bold rounded-lg text-sm transition-all hover:bg-ertc-primary/90 disabled:opacity-50 disabled:cursor-not-allowed shadow-md'
            >
              {consentLoading ? 'Please wait...' : 'I Agree — Continue to Document'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='w-full self-stretch h-screen flex flex-col overflow-hidden bg-white dark:bg-slate-900 transition-colors duration-300' ref={containerRef}>
      {/* Header */}
      <div className='shrink-0 px-4 py-3 bg-gray-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between transition-colors duration-300'>
        <h2 className='text-sm font-bold text-gray-800 dark:text-slate-100 truncate'>{templateName}</h2>
        <div className='flex items-center gap-2'>
          {!allComplete ? (
            <>
              <span className='text-xs font-medium text-amber-600'>
                {incompleteRequired.length} required field{incompleteRequired.length !== 1 ? 's' : ''} remaining
              </span>
              <button
                onClick={handleGoToNext}
                className='px-3 py-1.5 bg-amber-500 text-white font-bold rounded-lg text-xs transition-all hover:bg-amber-600'
              >
                Next →
              </button>
            </>
          ) : (
            <button
              onClick={handleFinish}
              disabled={submitting}
              className='px-5 py-1.5 bg-green-600 text-white font-bold rounded-lg text-sm transition-all hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {submitting ? 'Submitting...' : 'Finish ✓'}
            </button>
          )}
        </div>
      </div>

      {/* Scrollable PDF viewer */}
      <div ref={scrollRef} className='flex-1 min-h-0 overflow-y-auto bg-gray-100 dark:bg-slate-900/50 transition-colors duration-300'>
        <div className='flex justify-center py-4 px-4'>
          <div className='relative inline-block shadow-xl rounded-lg overflow-visible bg-white'>
            <Document
              file={pdfDataUri}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={
                <div className='flex items-center justify-center p-12'>
                  <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] border-gray-200 dark:border-slate-700 border-t-ertc-primary dark:border-t-ertc-primary h-8 w-8 animate-spin'></div>
                </div>
              }
            >
              <Page
                pageNumber={currentPage}
                width={pageWidth}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            </Document>

            {/* Interactive field overlays */}
            {currentPageFields.map(field => {
              if (field.type === 'signature') {
                const isSigned = signedFields[field.fieldId]
                return (
                  <button
                    key={field.fieldId}
                    onClick={() => setSignModalFieldId(field.fieldId)}
                    className={`absolute cursor-pointer transition-all group ${highlightedFieldId === field.fieldId ? 'z-10 animate-pulse ring-2 ring-amber-400 ring-offset-1 rounded' : ''}`}
                    style={{
                      left: `${field.x}%`,
                      top: `${field.y}%`,
                      width: `${field.width}%`,
                      height: `${field.height}%`
                    }}
                    title='Click to sign'
                  >
                    <div className={`w-full h-full border-2 border-dashed rounded flex items-center justify-center transition-colors
                      ${isSigned
                        ? 'border-green-400 bg-green-50/60'
                        : 'border-blue-400 bg-blue-50/40 group-hover:bg-blue-100/60'
                      }`}
                    >
                      {isSigned ? (
                        <img src={isSigned} alt='Signature' className='w-full h-full object-contain p-0.5' />
                      ) : (
                        <span className='text-blue-500 font-semibold text-xs animate-pulse'>Click to Sign</span>
                      )}
                    </div>
                  </button>
                )
              }

              if (field.type === 'editable') {
                return (
                  <div
                    key={field.fieldId}
                    className='absolute'
                    style={{
                      left: `${field.x}%`,
                      top: `${field.y}%`,
                      width: `${field.width}%`,
                      height: `${field.height}%`
                    }}
                  >
                    <input
                      type='text'
                      value={fieldValues[field.fieldId] || ''}
                      onChange={(e) => setFieldValues(prev => ({ ...prev, [field.fieldId]: e.target.value }))}
                      maxLength={field.maxLength || undefined}
                      placeholder={field.label || 'Enter value'}
                      className={`w-full h-full px-1 bg-yellow-50/80 border rounded text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-400/60 focus:bg-yellow-50 ${highlightedFieldId === field.fieldId ? 'border-amber-500 ring-2 ring-amber-400 animate-pulse' : 'border-yellow-300'}`}
                      style={{ fontSize: `${Math.max(8, (field.fontSize || 16) * 0.6) * pdfScale}px` }}
                    />
                  </div>
                )
              }

              if (field.type === 'multiline') {
                return (
                  <div
                    key={field.fieldId}
                    className='absolute'
                    style={{
                      left: `${field.x}%`,
                      top: `${field.y}%`,
                      width: `${field.width}%`,
                      height: `${field.height}%`
                    }}
                  >
                    <textarea
                      value={fieldValues[field.fieldId] || ''}
                      onChange={(e) => setFieldValues(prev => ({ ...prev, [field.fieldId]: e.target.value }))}
                      maxLength={field.maxLength || undefined}
                      placeholder={field.label || 'Enter text'}
                      className={`w-full h-full px-1 py-0.5 bg-yellow-50/80 border rounded text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-400/60 focus:bg-yellow-50 resize-none ${highlightedFieldId === field.fieldId ? 'border-amber-500 ring-2 ring-amber-400 animate-pulse' : 'border-yellow-300'}`}
                      style={{ fontSize: `${Math.max(8, (field.fontSize || 16) * 0.6) * pdfScale}px`, lineHeight: 1.3 }}
                    />
                  </div>
                )
              }

              if (field.type === 'checkbox') {
                const isChecked = fieldValues[field.fieldId] === true || fieldValues[field.fieldId] === 'true'
                return (
                  <button
                    key={field.fieldId}
                    onClick={() => setFieldValues(prev => ({ ...prev, [field.fieldId]: !isChecked }))}
                    className={`absolute cursor-pointer transition-all ${highlightedFieldId === field.fieldId ? 'z-10 animate-pulse ring-2 ring-amber-400 ring-offset-1 rounded' : ''}`}
                    style={{
                      left: `${field.x}%`,
                      top: `${field.y}%`,
                      width: `${field.width}%`,
                      height: `${field.height}%`
                    }}
                    title={isChecked ? 'Click to uncheck' : 'Click to check'}
                  >
                    <div className={`w-full h-full border-2 rounded flex items-center justify-center transition-all
                      ${isChecked
                        ? 'border-green-500 bg-green-50/80'
                        : 'border-gray-400 bg-white/60 hover:border-blue-400 hover:bg-blue-50/40'
                      }`}
                    >
                      {isChecked && (
                        <svg className='w-full h-full p-[1px] text-green-600' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='4' strokeLinecap='round' strokeLinejoin='round'>
                          <polyline points='20 6 9 17 4 12' />
                        </svg>
                      )}
                    </div>
                  </button>
                )
              }

              return null
            })}
          </div>
        </div>
      </div>

      {/* Footer: page navigation */}
      {numPages > 1 && (
        <div className='shrink-0 flex items-center justify-center gap-3 py-2 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 transition-colors duration-300'>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
            className='px-3 py-1.5 bg-white dark:bg-slate-700 dark:text-slate-200 border border-gray-200 dark:border-slate-600 rounded-lg text-xs font-medium disabled:opacity-40 transition-colors'
          >
            ← Prev
          </button>
          <span className='text-xs font-medium text-gray-500 dark:text-slate-400'>
            Page {currentPage} of {numPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
            disabled={currentPage >= numPages}
            className='px-3 py-1.5 bg-white dark:bg-slate-700 dark:text-slate-200 border border-gray-200 dark:border-slate-600 rounded-lg text-xs font-medium disabled:opacity-40 transition-colors'
          >
            Next →
          </button>
        </div>
      )}

      {/* ── Signature Modal ── */}
      {signModalFieldId && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'>
          <div className='bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden'>
            <div className='flex items-center justify-between px-6 py-4 border-b border-gray-100'>
              <h3 className='text-lg font-bold text-gray-900'>Sign Document</h3>
              <button
                onClick={() => setSignModalFieldId(null)}
                className='text-gray-400 hover:text-gray-600 text-xl leading-none transition-colors'
              >
                ×
              </button>
            </div>

            <div className='px-6 py-5'>
              <p className='text-sm text-gray-500 mb-3'>
                Draw your signature below using your mouse or finger.
              </p>
              <div className='border-2 border-gray-200 rounded-xl overflow-hidden bg-gray-50'>
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={180}
                  className='w-full cursor-crosshair touch-none'
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
              <div className='flex items-center justify-between mt-3'>
                <button
                  onClick={clearCanvas}
                  className='text-xs text-red-500 hover:text-red-700 font-medium transition-colors'
                >
                  Clear
                </button>
                <span className='text-[11px] text-gray-400'>
                  {hasCanvasContent ? 'Signature captured' : 'No signature yet'}
                </span>
              </div>
            </div>

            <div className='flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50'>
              <button
                onClick={() => setSignModalFieldId(null)}
                className='px-5 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors'
              >
                Cancel
              </button>
              <button
                onClick={applySignature}
                disabled={!hasCanvasContent}
                className='px-6 py-2.5 bg-ertc-primary text-white font-bold rounded-lg text-sm transition-all hover:bg-ertc-primary/90 disabled:opacity-40 disabled:cursor-not-allowed shadow-md'
              >
                Apply Signature
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
